import { useState, useEffect } from "react";
import { generateCashFlowProjection } from "../utils/irrCalculator";
import {
  resolveManagementFeePercent,
  MANAGEMENT_FEE_START_YEAR,
} from "../utils/rentalAnalysisCalculator";
import IRRExplainerModal from "./IRRExplainerModal";

export default function IRRModal({ isOpen, onClose, property, prediction, propertyAnalysis, mlRent, agentRent, userRent, discountedPrice, discountPct, computedValues }) {
  // Default assumptions
  const [rentGrowthRate, setRentGrowthRate] = useState(5); // Changed from 4% to 5%
  const [propertyAppreciationRate, setPropertyAppreciationRate] = useState(6);

  // Calculate projection for I/O mortgage (10 year projection)
  const [projection, setProjection] = useState(null);
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Management fee charged by the projection below. Derived, not state: the table and the
  // notes have to quote the same figure the projection was run on, and a second source would
  // let them disagree for a render. computedValues already resolved it from the saved
  // analysis; the fallback covers a caller passing an older computedValues shape.
  const managementFeePercent = computedValues?.managementFeePercent
    ?? resolveManagementFeePercent(propertyAnalysis);

  useEffect(() => {
    if (!isOpen || !property || !prediction || !computedValues) return;

    // Calculate MAX rent from all sources
    const rents = [
      userRent || 0,  // User edited rent value (from checklist high range)
      mlRent || 0,    // ML prediction
      agentRent || 0, // AI/Agent prediction
      prediction.predicted_rent || 0 // Fallback to prediction object
    ];
    const monthlyRent = Math.max(...rents);

    // Use centralized computed values from parent (single source of truth)
    const propertyPrice = computedValues.discountedPrice;
    const monthlyCarryingCosts = computedValues.monthlyCarryingCosts;
    const monthlyMortgagePayment = computedValues.monthlyMortgagePayment;
    const totalOutOfPocket = computedValues.totalOutOfPocket;
    const mortgageRate = computedValues.mortgageRate;
    const paymentType = computedValues.paymentType;
    const downPaymentPercent = computedValues.downPaymentPercent;

    const result = generateCashFlowProjection({
      propertyPrice,
      monthlyRent,
      monthlyCarryingCosts,
      totalOutOfPocket,
      monthlyMortgagePayment,
      mortgageRate,
      mortgageTerm: 30, // Always 30 year total term
      paymentType,
      downPaymentPercent,
      rentGrowthRate,
      propertyAppreciationRate,
      years: 10,
      managementFeePercent,
      managementFeeStartYear: MANAGEMENT_FEE_START_YEAR,
    });

    setProjection(result);
  }, [isOpen, property, prediction, computedValues, managementFeePercent, mlRent, agentRent, userRent, rentGrowthRate, propertyAppreciationRate]);

  if (!isOpen) return null;

  // Modal overlay and container styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "1200px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "16px",
  };

  const titleStyle = {
    fontSize: "28px",
    fontWeight: "700",
    color: "#333",
  };

  const closeButtonStyle = {
    fontSize: "28px",
    fontWeight: "700",
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "#666",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const summaryBoxStyle = {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "24px",
    border: "2px solid #000",
  };

  const inputsContainerStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  };

  const inputStyle = {
    padding: "10px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    textAlign: "right",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  };

  const thStyle = {
    textAlign: "right",
    padding: "10px 8px",
    borderBottom: "2px solid #000",
    fontWeight: "700",
    color: "#333",
    backgroundColor: "#f8f9fa",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const tdStyle = {
    textAlign: "right",
    padding: "8px",
    borderBottom: "1px solid #e0e0e0",
  };

  const firstColumnStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: "600",
  };

  const summaryItemStyle = {
    textAlign: "center",
  };

  const summaryLabelStyle = {
    fontSize: "12px",
    color: "#666",
    marginBottom: "4px",
  };

  const summaryValueStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#333",
  };

  return (
    <>
    <IRRExplainerModal
      isOpen={explainerOpen}
      onClose={() => setExplainerOpen(false)}
      rentGrowthRate={rentGrowthRate}
      propertyAppreciationRate={propertyAppreciationRate}
      managementFeePercent={managementFeePercent}
    />
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            IRR Analysis Report
            <button
              onClick={() => setExplainerOpen(true)}
              style={{ marginLeft: "14px", fontSize: "13px", fontWeight: "500",
                       background: "none", border: "none", color: "#1976d2",
                       textDecoration: "underline", cursor: "pointer", padding: 0 }}
            >
              How is this calculated?
            </button>
          </h2>
          <button style={closeButtonStyle} onClick={onClose}>×</button>
        </div>

        {/* IRR Summary */}
        {projection && (
          <>
            <div style={summaryBoxStyle}>
              {/* IRR Display */}
              <div style={{ fontSize: "48px", fontWeight: "700", color: projection.irr >= 0 ? "#28a745" : "#dc3545", textAlign: "center", marginBottom: "24px" }}>
                {projection.irr.toFixed(2)}% IRR
              </div>

              {/* Year 7 vs Year 10 Comparison */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                  {/* Year 7 Exit */}
                  <div style={{ padding: "16px", backgroundColor: "#fff", borderRadius: "8px", border: "2px solid #ddd" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", textAlign: "center" }}>
                      7-Year Exit
                    </div>
                    {(() => {
                      const year7CashFlow = projection.yearlyData.slice(0, 7).reduce((sum, d) => sum + d.cashFlow, 0);
                      // equityIfSold, not equity: this panel IS an exit, and `equity` is only
                      // net of the 6.825% selling cost in the projection's final year -- which
                      // is year 10 here, not year 7. Reading the raw figure overstated the
                      // 7-year return by 36 points on a $1M deal ($753,630 of equity against
                      // the $651,007 a sale would actually hand back).
                      const year7Equity = projection.yearlyData[6].equityIfSold;
                      const year7TotalReturn = year7CashFlow + year7Equity;
                      const year7ReturnPct = ((year7TotalReturn - projection.initialInvestment) / projection.initialInvestment) * 100;

                      return (
                        <>
                          <div style={{ fontSize: "32px", fontWeight: "700", color: "#28a745", textAlign: "center", marginBottom: "12px" }}>
                            {year7ReturnPct.toFixed(1)}%
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: "13px", color: "#666" }}>Total Cash Flow:</span>
                              <span style={{ fontSize: "14px", fontWeight: "700" }}>
                                ${year7CashFlow.toLocaleString()}
                              </span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: "13px", color: "#666" }}>Equity after sale costs:</span>
                              <span style={{ fontSize: "14px", fontWeight: "700" }}>
                                ${year7Equity.toLocaleString()}
                              </span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "8px", borderTop: "1px solid #ddd" }}>
                              <span style={{ fontSize: "13px", color: "#666" }}>Total Return:</span>
                              <span style={{ fontSize: "16px", fontWeight: "700", color: "#28a745" }}>
                                ${year7TotalReturn.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Year 10 Exit */}
                  <div style={{ padding: "16px", backgroundColor: "#fff", borderRadius: "8px", border: "2px solid #000" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", textAlign: "center" }}>
                      10-Year Exit
                    </div>
                    <div style={{ fontSize: "32px", fontWeight: "700", color: "#28a745", textAlign: "center", marginBottom: "12px" }}>
                      {projection.totalReturnPercent.toFixed(1)}%
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: "13px", color: "#666" }}>Total Cash Flow:</span>
                        <span style={{ fontSize: "14px", fontWeight: "700" }}>
                          ${projection.totalCashFlow.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: "13px", color: "#666" }}>Equity after sale costs:</span>
                        <span style={{ fontSize: "14px", fontWeight: "700" }}>
                          ${projection.finalEquity.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "8px", borderTop: "1px solid #ddd" }}>
                        <span style={{ fontSize: "13px", color: "#666" }}>Total Return:</span>
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "#28a745" }}>
                          ${projection.totalReturn.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Info */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px", marginBottom: "16px" }}>
                <div style={summaryItemStyle}>
                  <div style={summaryLabelStyle}>Initial Investment</div>
                  <div style={summaryValueStyle}>${projection.initialInvestment.toLocaleString()}</div>
                </div>
              </div>

              <div style={{ marginTop: "16px", textAlign: "center", color: "#666", fontSize: "14px" }}>
                <strong>Starting Rent:</strong> ${projection.startingRent.toLocaleString()}/month
                {" | "}
                <strong>Mortgage:</strong> ${projection.mortgageAmount.toLocaleString()} loan =
                ${projection.monthlyMortgagePayment.toLocaleString()}/month
                {" | "}
                <strong>Monthly Carrying Costs:</strong> HOA + Taxes =
                ${projection.monthlyCarryingCosts.toLocaleString()}/month
              </div>
              {discountPct > 0 && (
                <div style={{ marginTop: "8px", textAlign: "center", color: "#28a745", fontSize: "13px", fontWeight: "600" }}>
                  ✓ Price includes {discountPct.toFixed(1)}% discount applied
                </div>
              )}
            </div>

            {/* Editable Assumptions */}
            <div style={inputsContainerStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Annual Cash Flow Growth (%)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={rentGrowthRate}
                  onChange={(e) => setRentGrowthRate(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="20"
                  step="0.5"
                />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Property Appreciation (%)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={propertyAppreciationRate}
                  onChange={(e) => setPropertyAppreciationRate(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="20"
                  step="0.5"
                />
              </div>
            </div>

            {/* Year-by-Year Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left" }}>Year</th>
                    {managementFeePercent > 0 && <th style={thStyle}>Mgmt Fee</th>}
                    <th style={thStyle}>Cash Flow</th>
                    <th style={thStyle}>Property Value</th>
                    <th style={thStyle}>Appreciation</th>
                    <th style={thStyle}>Equity</th>
                  </tr>
                </thead>
                <tbody>
                  {projection.yearlyData.map((data) => (
                    <tr key={data.year} style={{ backgroundColor: data.year === 10 ? "#fff3cd" : "transparent" }}>
                      <td style={firstColumnStyle}>Year {data.year}</td>
                      {managementFeePercent > 0 && (
                        <td style={{ ...tdStyle, color: "#dc3545" }}>
                          {data.managementFee > 0 ? `-$${data.managementFee.toLocaleString()}` : "-"}
                        </td>
                      )}
                      <td style={{ ...tdStyle, fontWeight: "700", color: data.cashFlow >= 0 ? "#28a745" : "#dc3545" }}>
                        ${data.cashFlow.toLocaleString()}
                      </td>
                      <td style={tdStyle}>${data.propertyValue.toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: "#28a745" }}>${data.appreciation.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: "700" }}>${data.equity.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            <div style={{ marginTop: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px", fontSize: "13px", color: "#666" }}>
              <strong>Notes:</strong>
              <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                <li>IRR calculation assumes property is sold at the end of Year 10</li>
                <li>Starting rent is the MAX of user input, ML prediction, or AI prediction</li>
                <li>Initial investment includes: Down Payment + Net Closing Costs + Renovation + (Time to Renovate × Monthly Carrying Costs)</li>
                <li>Selling costs of {projection.sellingCostPercent}% are deducted from the property value in BOTH exit figures — the 7-year and the 10-year</li>
                <li>The Equity column in the table below is equity held, before selling costs; the two exit boxes above are net of them</li>
                <li>Carrying costs include HOA and property taxes</li>
                <li>
                  Cash flow is calculated as: Rent - Carrying Costs - Mortgage Payment
                  {managementFeePercent > 0 && " - Management Fee"}
                </li>
                <li>Cash flow grows at 5% annually (not rent) - rent adjusts to maintain cash flow growth</li>
                <li>Default assumptions: Cash flow growth: 5%, Property appreciation: 6%</li>
                {managementFeePercent > 0 && (
                  <li>
                    <strong>
                      Includes {managementFeePercent}% management fees from year{" "}
                      {MANAGEMENT_FEE_START_YEAR} onwards
                    </strong>{" "}
                    — charged on rent collected. Year 1 is the lease-up and is not charged.
                    Edit it in IRR Assumptions.
                  </li>
                )}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
